import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { SectionTitle, StatCard, ProgressBar, EmptyState, StatusPill } from "../../components/Atoms";
import { Sparkle, Stack, Heart, ClipboardText, CheckCircle, CurrencyDollarSimple, Trophy, WarningCircle, ArrowRight, Megaphone, ChartBar } from "@phosphor-icons/react";
import { useAuth } from "../../AuthContext";
import { Link } from "react-router-dom";

const TABS = [
  { key: "available", label: "Available", color: "#22D3EE" },
  { key: "applied",   label: "Applied",   color: "#F59E0B" },
  { key: "live",      label: "Live",      color: "#A3E635" },
  { key: "completed", label: "Completed", color: "#8B5CF6" },
];

export default function AmbassadorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [projectsByTab, setProjectsByTab] = useState({});
  const [tab, setTab] = useState("available");
  const [announcements, setAnnouncements] = useState([]);
  const [polls, setPolls] = useState([]);

  const [loadError, setLoadError] = useState(null);

  const load = React.useCallback(() => {
    setLoadError(null);
    Promise.all([
      api.get("/stats/ambassador"),
      api.get("/me/profile"),
      api.get("/me/earnings"),
      api.get("/me/projects?tab=available"),
      api.get("/me/projects?tab=applied"),
      api.get("/me/projects?tab=live"),
      api.get("/me/projects?tab=completed"),
      api.get("/announcements"),
      api.get("/polls"),
    ]).then(([s, pr, e, av, ap, lv, cp, an, po]) => {
      setStats(s.data); setProfile(pr.data); setEarnings(e.data);
      setProjectsByTab({ available: av.data, applied: ap.data, live: lv.data, completed: cp.data });
      setAnnouncements(an.data || []);
      setPolls(po.data || []);
    }).catch((err) => {
      setLoadError(formatErr(err));
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <div className="glass p-8 flex flex-col items-center gap-3 text-center">
        <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
        <div className="text-white font-bold">Couldn't load your dashboard</div>
        <div className="text-muted text-sm">{loadError}</div>
        <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
      </div>
    );
  }

  if (!stats || !profile || !earnings) return <div className="skeleton h-64" />;

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const incomplete = (profile.completion_pct || 0) < 70;
  const pending = !!profile.pending_approval;
  const currentList = projectsByTab[tab] || [];
  const latestAnn = (announcements || []).slice(0, 2);
  const latestPolls = (polls || []).slice(0, 2);
  const showPolls = (polls || []).length > 0;
  const gridCols = showPolls ? "lg:grid-cols-3" : "lg:grid-cols-2";

  return (
    <div className="space-y-6">
      {/* Greeting + earnings banner + announcements + polls */}
      <div className={`grid gap-3 ${gridCols}`}>
        {/* Column 1: Name card */}
        <div className="glass p-5 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-[#EC4899]/30 to-[#8B5CF6]/10 blur-3xl" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#A3E635] flex items-center gap-2"><Sparkle size={14} weight="fill" /> Creator HQ</div>
            <h1 className="text-2xl sm:text-3xl font-black text-white mt-2" data-testid="amb-greeting">Hi {firstName} 👋</h1>
            <div className="text-muted text-sm mt-2">
              Earned <span className="text-[#A3E635] font-black">{fmtMoney(earnings.approved_earnings)}</span>
            </div>
            <div className="text-[11px] text-muted mt-1">Wallet: <span className="text-[#10B981] font-bold">{fmtMoney(earnings.wallet_balance)}</span> · Potential: <span className="text-[#22D3EE] font-bold">{fmtMoney(earnings.potential_earnings)}</span></div>
          </div>
        </div>

        {/* Column 2: Announcements */}
        <div className="glass p-5 flex flex-col" data-testid="dash-announcements">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-white font-bold text-sm"><Megaphone size={16} weight="bold" className="text-[#EC4899]" /> Latest announcements</div>
            <Link to="/me/updates" className="text-[11px] text-[#22D3EE] font-bold flex items-center gap-1" data-testid="dash-view-all-announcements">View all <ArrowRight size={10} weight="bold" /></Link>
          </div>
          {latestAnn.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-xs text-muted">No new announcements</div>
          ) : (
            <div className="space-y-2 flex-1">
              {latestAnn.map(a => (
                <Link key={a.id} to="/me/updates" className="block rounded-xl bg-white/5 hover:bg-white/10 p-2.5" data-testid={`dash-ann-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-white text-xs font-bold truncate">{a.title}</div>
                    <div className="text-[10px] text-muted whitespace-nowrap">{fmtDate(a.created_at)}</div>
                  </div>
                  <div className="text-[10px] text-muted truncate">{a.brand_name}</div>
                  <div className="text-[11px] text-white/80 truncate mt-0.5">{a.message}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Conditional Column 3: Polls */}
        {showPolls && (
          <div className="glass p-5 flex flex-col" data-testid="dash-polls">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-white font-bold text-sm"><ChartBar size={16} weight="bold" className="text-[#A3E635]" /> Latest polls</div>
              <Link to="/me/updates" className="text-[11px] text-[#A3E635] font-bold flex items-center gap-1" data-testid="dash-view-all-polls">View all <ArrowRight size={10} weight="bold" /></Link>
            </div>
            <div className="space-y-2 flex-1">
              {latestPolls.map(p => (
                <div key={p.id} className="rounded-xl bg-white/5 p-2.5" data-testid={`dash-poll-${p.id}`}>
                  <div className="text-white text-xs font-bold truncate">{p.question}</div>
                  <div className="text-[10px] text-muted truncate">{p.brand_name}</div>
                  <Link to="/me/updates" className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#A3E635]" data-testid={`dash-vote-${p.id}`}>{p.my_vote ? "View" : "Vote now"} <ArrowRight size={10} weight="bold" /></Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pending banner */}
      {pending && (
        <div className="glass p-4 border-l-4" style={{ borderLeftColor: "#F59E0B" }} data-testid="dash-pending-banner">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="fill" color="#F59E0B" />
            <div className="flex-1">
              <div className="text-white font-bold">Your profile is pending approval from the TYC Team.</div>
              <div className="text-xs text-muted mt-1">For urgent help contact support. You&apos;ll be able to apply once approved.</div>
            </div>
            <Link to="/me/profile" className="btn-ghost text-xs flex items-center gap-1.5">View profile <ArrowRight size={12} weight="bold" /></Link>
          </div>
        </div>
      )}

      {/* Profile completion banner */}
      {!pending && incomplete && (
        <div className="glass p-4 border-l-4" style={{ borderLeftColor: "#22D3EE" }} data-testid="profile-completion-banner">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="fill" color="#22D3EE" />
            <div className="flex-1">
              <div className="text-white font-bold">Complete your profile so that you can apply to campaigns and improve your selection chances.</div>
              <div className="mt-2 max-w-sm">
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-muted uppercase font-bold tracking-wider">{profile.completion_pct || 0}% done</span>
                </div>
                <ProgressBar value={profile.completion_pct || 0} max={100} color="#22D3EE" />
              </div>
            </div>
            <Link to="/me/profile" className="btn-primary text-xs flex items-center gap-1.5" data-testid="complete-profile-cta">Complete <ArrowRight size={12} weight="bold" /></Link>
          </div>
        </div>
      )}

      {/* Stat row — Live first, Available second */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard label="Live" value={stats.live_projects} icon={Heart} accent="pink" />
        <StatCard label="Available" value={stats.available_projects} icon={Stack} accent="cyan" />
        <StatCard label="Applied" value={stats.applied_pending || 0} icon={ClipboardText} accent="amber" />
        <StatCard label="Completed" value={stats.completed_projects || 0} icon={CheckCircle} accent="lime" />
        <StatCard label="Earnings" value={fmtMoney(stats.total_earnings)} icon={CurrencyDollarSimple} accent="green" />
        <StatCard label="Pending ₹" value={fmtMoney(stats.pending_earnings)} icon={CurrencyDollarSimple} accent="purple" />
      </div>

      {/* Live + Available sections side-by-side */}
      <div className="grid lg:grid-cols-2 gap-5">
        <DashSection
          title="Live projects"
          subtitle="Campaigns you're already in — keep delivering"
          accent="#A3E635"
          list={projectsByTab.live || []}
          empty="No live projects yet. Apply to one to start earning."
          status="Live"
        />
        <DashSection
          title="Available projects"
          subtitle="Open campaigns you can apply to"
          accent="#22D3EE"
          list={projectsByTab.available || []}
          empty="No new projects right now. Check back soon."
          status="Active"
        />
      </div>

      {/* Leaderboard link */}
      <Link to="/me/leaderboard" className="glass p-4 hover-lift flex items-center gap-3" data-testid="dash-leaderboard-link">
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#F59E0B]/15"><Trophy size={18} weight="fill" color="#F59E0B" /></div>
        <div className="flex-1">
          <div className="text-white font-bold">See where you rank</div>
          <div className="text-xs text-muted">Top creators leaderboard</div>
        </div>
        <ArrowRight size={16} weight="bold" className="text-muted" />
      </Link>
    </div>
  );
}

function DashSection({ title, subtitle, accent, list, empty, status }) {
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-full" style={{ background: accent, boxShadow: `0 0 10px ${accent}` }} />
        <div className="text-white font-bold">{title}</div>
        <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: accent, background: `${accent}15`, border: `1px solid ${accent}40` }}>{list.length}</span>
      </div>
      <div className="text-xs text-muted mb-3">{subtitle}</div>
      {list.length === 0 ? <div className="text-sm text-muted italic py-3">{empty}</div> : (
        <div className="space-y-2">
          {list.slice(0, 3).map(p => (
            <Link key={p.id} to={`/me/projects/${p.id}`} className="block p-3 rounded-2xl bg-white/3 border border-white/5 hover:border-white/15 transition" data-testid={`dash-${title.split(" ")[0].toLowerCase()}-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold text-sm truncate">{p.title}</div>
                  <div className="text-[11px] text-muted truncate">{p.client_name}</div>
                </div>
                <StatusPill status={status} />
              </div>
              {status === "Live" ? (
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-muted mb-1">
                    <span>{p.my_approved || 0}/{p.task_count || 0} tasks</span>
                    <span>{fmtMoney(p.total_reward || 0)}</span>
                  </div>
                  <ProgressBar value={p.my_approved || 0} max={p.task_count || 1} color={accent} />
                </div>
              ) : (
                <div className="text-[11px] text-muted mt-1.5">{p.task_count || 0} tasks · {fmtMoney(p.total_reward || 0)}</div>
              )}
            </Link>
          ))}
          {list.length > 3 && (
            <Link to="/me/projects" className="block text-center text-xs text-[#22D3EE] font-bold py-1">View all {list.length} →</Link>
          )}
        </div>
      )}
    </div>
  );
}
